import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faCrosshairs,
  faGear,
  faLocationDot,
  faSpinner,
  faStop,
  faTowerBroadcast,
} from "@fortawesome/free-solid-svg-icons";
import {
  GoogleMap,
  useJsApiLoader,
  Circle,
  Marker,
} from "@react-google-maps/api";
import { Skeleton } from "@mui/material";
import { PulseLoader } from "react-spinners";
import styles from "./GPSLocationTracking.module.css";
import { useSelector } from "react-redux";

const mapLibraries = ["drawing", "places", "geometry"];
const GEO_SETTINGS_PATH = "/settings/geolocation";

export const DEFAULT_MAP_LAT_LNG = { lat: 37.33479, lng: -121.88814 };

const GPSLocationTracking = ({
  center,
  setCircle,
  radius,
  zoom,
  height,
  fetchCurrentLocation,
  startTracking,
  stopTracking,
  trackingStatus,
  oneTimeTracking,
  endingTime,
  lastUpdateTime,
  enableLocationAccess,
  updateGeoLocationSettings,
  handleShowLocationModal,
  appModules,
  batteryPer,
  deviceConnectedForLiveTracking,
  isReadOnly,
  locationLoading = false,
}) => {
  const { isLoaded, error } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_NEXT_PUBLIC_GOOGLE_MAPS_API,
    libraries: mapLibraries,
    version: "3.64",
  });
  const history = useHistory();
  const user = useSelector((state) => state.user);
  const isAndroidDevice =
    user?.deviceType === 2 || user?.deviceType === "2";
  const [map, setMap] = useState();
  const mapPaneRef = useRef(null);
  const [circleData, setCircleData] = useState(
    center?.lng && center?.lat
      ? { center, radius }
      : { center: DEFAULT_MAP_LAT_LNG, radius: 0 },
  );

  const refreshMapLayout = useCallback(
    (mapInstance) => {
      const m = mapInstance || map;
      if (!m || !window.google?.maps?.event) return;
      window.google.maps.event.trigger(m, "resize");
      const target = center || circleData?.center || DEFAULT_MAP_LAT_LNG;
      if (target?.lat != null && target?.lng != null) {
        m.setCenter(target);
      }
    },
    [map, center, circleData?.center],
  );

  useEffect(() => {
    setCircleData(
      center?.lng && center?.lat
        ? { center, radius }
        : { center: DEFAULT_MAP_LAT_LNG, radius: null },
    );
  }, [center, radius]);

  useEffect(() => {
    if (circleData?.center?.lng && circleData?.center?.lat) {
      setCircle(circleData);
    } else {
      setCircle({ center: DEFAULT_MAP_LAT_LNG, radius: 0 });
    }
  }, [circleData, setCircle]);

  useEffect(() => {
    if (!map) return;
    refreshMapLayout(map);
    const frame = window.requestAnimationFrame(() => refreshMapLayout(map));
    const timer = window.setTimeout(() => refreshMapLayout(map), 250);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [map, refreshMapLayout]);

  useEffect(() => {
    if (!map) return undefined;
    const onResize = () => refreshMapLayout(map);
    let observer;
    if (mapPaneRef.current && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onResize);
      observer.observe(mapPaneRef.current);
    }
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("resize", onResize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("resize", onResize);
    };
  }, [map, refreshMapLayout]);

  useEffect(() => {
    if (center) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        map?.setCenter(pos);
        map?.setZoom(zoom);
      },
      () => {
        map?.setCenter(DEFAULT_MAP_LAT_LNG);
        map?.setZoom(2);
      },
    );
  }, [map, center, zoom]);

  const handleMapLoad = useCallback(
    (m) => {
      setMap(m);
      window.setTimeout(() => refreshMapLayout(m), 50);
      window.setTimeout(() => refreshMapLayout(m), 300);
    },
    [refreshMapLayout],
  );

  const handleTracking = () => {
    if (!enableLocationAccess && !isAndroidDevice) {
      updateGeoLocationSettings();
      return;
    }
    if (!trackingStatus) {
      startTracking();
    } else {
      stopTracking();
    }
  };

  const batteryLow =
    batteryPer?.data?.value !== null &&
    batteryPer?.data?.value !== undefined &&
    batteryPer.data.value <= 5;

  const locationAccessRequired = !enableLocationAccess && !isAndroidDevice;
  const liveTrackingBlocked = batteryLow || locationAccessRequired;

  const hasLocation = center !== null && !!lastUpdateTime;
  const isLive = trackingStatus && deviceConnectedForLiveTracking;
  const isConnecting = trackingStatus && !deviceConnectedForLiveTracking;
  const currentLocationDisabled =
    oneTimeTracking || batteryLow || isConnecting || trackingStatus;
  const liveTrackingDisabled = trackingStatus
    ? false
    : liveTrackingBlocked || oneTimeTracking;
  const historyDisabled = oneTimeTracking || isConnecting || trackingStatus;

  const circleRef = useRef();
  const listenersRef = useRef([]);

  const onEditCircle = useCallback(() => {
    if (circleRef.current) {
      const _center = circleRef.current.getCenter();
      setCircle({
        center: { lat: _center?.lat(), lng: _center?.lng() },
        radius: circleRef.current.getRadius() | 0,
      });
    }
  }, [setCircle]);

  const onCircleLoad = useCallback(
    (circle) => {
      circleRef.current = circle;
      listenersRef.current.push(
        circle.addListener("set_at", onEditCircle),
        circle.addListener("insert_at", onEditCircle),
        circle.addListener("remove_at", onEditCircle),
      );
      if (center) {
        const m = circleRef.current.getMap();
        m?.fitBounds(circleRef.current.getBounds());
      }
    },
    [onEditCircle, center],
  );

  const onCircleUnmount = useCallback(() => {
    listenersRef.current.forEach((lis) => lis?.remove());
    circleRef.current = undefined;
  }, []);

  if (!isLoaded || error) {
    return (
      <div className={styles.skeletonWrap}>
        <Skeleton variant="rectangular" animation="wave" height={200} />
      </div>
    );
  }

  if (locationLoading) {
    return (
      <div className={styles.mapLoading} style={{ height: height || "100%" }}>
        <PulseLoader
          color="#0077b6"
          loading
          aria-label="Loading location"
          data-testid="location-loader"
        />
        <p className={styles.mapLoadingText}>Loading recent location…</p>
      </div>
    );
  }

  const hasGeoAccess = appModules?.active?.GPS?.includes(
    "GEO_LOCATION_TRACKING",
  );

  let statusMessage = "Recent GPS location not found";
  let statusTone = "muted";
  if (!hasGeoAccess) {
    statusMessage =
      "Safe location tracking is not available in your subscription plan.";
    statusTone = "warn";
  } else if (batteryLow) {
    statusMessage = `Live tracking unavailable — watch battery low (${batteryPer.data.value}% remaining). Please charge the watch.`;
    statusTone = "warn";
  } else if (locationAccessRequired && !isAndroidDevice) {
    statusMessage =
      "Enable Geo Location Tracking in settings to start live tracking.";
    statusTone = "warn";
  } else if (hasLocation) {
    statusMessage = `Last updated ${moment(lastUpdateTime).format(
      "MM/DD/YYYY hh:mm A",
    )}`;
    statusTone = "ok";
  }

  const liveTrackingClass = liveTrackingBlocked
    ? `${styles.btn} ${styles.btnMuted}`
    : trackingStatus
      ? `${styles.btn} ${styles.btnDanger}`
      : `${styles.btn} ${styles.btnPrimary}`;

  return (
    <div className={styles.root} style={{ height: height || "100%" }}>
      <div
        className={`${styles.mapPane} ${
          !hasGeoAccess || isReadOnly ? styles.mapPaneFill : ""
        }`}
        ref={mapPaneRef}
      >
        <div className={styles.mapOverlayLeft}>
          {statusMessage && (
            <div
              className={`${styles.statusChip} ${
                statusTone === "warn"
                  ? styles.statusChipWarn
                  : statusTone === "muted"
                    ? styles.statusChipMuted
                    : ""
              }`}
            >
              <span
                className={`${styles.statusDot} ${
                  statusTone === "warn"
                    ? styles.statusDotWarn
                    : statusTone === "muted"
                      ? styles.statusDotMuted
                      : ""
                }`}
              />
              <span>{statusMessage}</span>
            </div>
          )}

          {hasGeoAccess && !isReadOnly && (
            <button
              type="button"
              className={`${styles.settingsBtn} ${
                isLive ? styles.settingsBtnWithLive : ""
              }`}
              title="Geo location settings"
              aria-label="Open geo location settings"
              onClick={() => history.push(GEO_SETTINGS_PATH)}
            >
              <FontAwesomeIcon icon={faGear} />
            </button>
          )}
        </div>

        {isLive && (
          <div className={styles.liveBadge}>
            <span className={styles.livePulse} />
            Live · {endingTime} min left
          </div>
        )}

        <GoogleMap
          mapContainerClassName={styles.mapFill}
          mapContainerStyle={{ width: "100%", height: "100%" }}
          zoom={zoom}
          center={center || DEFAULT_MAP_LAT_LNG}
          onLoad={handleMapLoad}
          options={{
            mapTypeControl: false,
            draggable: true,
            scaleControl: false,
            scrollwheel: true,
            navigationControl: false,
            streetViewControl: false,
            rotateControl: false,
            cameraControl: false,
            zoomControl: true,
            zoomControlOptions: {
              position: window.google?.maps?.ControlPosition?.RIGHT_BOTTOM,
            },
            fullscreenControl: true,
            fullscreenControlOptions: {
              position: window.google?.maps?.ControlPosition?.RIGHT_BOTTOM,
            },
            clickableIcons: false,
            gestureHandling: "greedy",
          }}
        >
          {hasGeoAccess && circleData?.center && circleData?.radius && (
            <Circle
              editable={false}
              draggable={false}
              center={circleData.center}
              radius={circleData.radius}
              onLoad={onCircleLoad}
              onUnmount={onCircleUnmount}
              options={{
                fillColor: "#009eee",
                strokeColor: "#009eee",
                strokeWeight: 1,
              }}
            />
          )}
          {hasGeoAccess && center && <Marker position={center} />}
        </GoogleMap>
      </div>

      {!isReadOnly && hasGeoAccess && (
        <aside className={styles.sidebar}>
          <section className={styles.featureCard}>
            <div className={styles.featureHead}>
              <div
                className={`${styles.featureIcon} ${styles.featureIconTeal}`}
              >
                <FontAwesomeIcon
                  icon={faLocationDot}
                  style={{ color: "#e03e3e" }}
                />
              </div>
              <div>
                <h3 className={styles.featureTitle}>Recent Live Location</h3>
                <p className={styles.featureDesc}>
                View or request the device’s current location.
                </p>
              </div>
            </div>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={fetchCurrentLocation}
              disabled={currentLocationDisabled}
              title={
                currentLocationDisabled
                  ? "Please wait for the current action to finish"
                  : "Fetch current location"
              }
            >
              {oneTimeTracking ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Fetching location…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCrosshairs} />
                  Get Current Location
                </>
              )}
            </button>
          </section>

          <section
            className={`${styles.featureCard} ${
              isLive ? styles.featureCardActive : ""
            }`}
          >
            <div className={styles.featureHead}>
              <div
                className={`${styles.featureIcon} ${
                  isLive ? styles.featureIconRed : styles.featureIconGreen
                }`}
              >
                <FontAwesomeIcon
                  icon={trackingStatus ? faStop : faTowerBroadcast}
                />
              </div>
              <div>
                <h3 className={styles.featureTitle}>Live Tracking</h3>
                <p className={styles.featureDesc}>
                Track the device’s location in real-time for a specified period.
                </p>
              </div>
            </div>

            {isLive && (
              <div className={styles.countdown}>
                <span>Session ending in</span>
                <span>{endingTime} min</span>
              </div>
            )}

            {isConnecting && (
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Status</span>
                <span className={styles.metaValue}>Connecting to device…</span>
              </div>
            )}

            <button
              type="button"
              className={liveTrackingClass}
              onClick={handleTracking}
              disabled={liveTrackingDisabled}
              title={
                trackingStatus
                  ? "Click to stop live tracking"
                  : batteryLow
                    ? "Live tracking unavailable while watch battery is low"
                    : locationAccessRequired
                      ? "Enable Geo Location Tracking in settings first"
                      : oneTimeTracking
                        ? "Please wait for the current action to finish"
                        : "Click to start live tracking"
              }
            >
              {isConnecting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Connecting…
                </>
              ) : trackingStatus ? (
                <>
                  <FontAwesomeIcon icon={faStop} />
                  Stop Live Tracking
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faTowerBroadcast} />
                  Start Live Tracking
                </>
              )}
            </button>
          </section>

          <section className={styles.featureCard}>
            <div className={styles.featureHead}>
              <div
                className={`${styles.featureIcon} ${styles.featureIconAmber}`}
              >
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </div>
              <div>
                <h3 className={styles.featureTitle}>Location Alert History</h3>
                <p className={styles.featureDesc}>
                View safe-location alerts from the past 7 days.
                </p>
              </div>
            </div>

            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={() => handleShowLocationModal(true)}
              disabled={historyDisabled}
              title={
                historyDisabled
                  ? "Please wait for the current action to finish"
                  : "Click to view location alert history"
              }
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
              View Location Alert History
            </button>
          </section>
        </aside>
      )}
    </div>
  );
};

GPSLocationTracking.propTypes = {
  zoom: PropTypes.number,
  center: PropTypes.object,
  radius: PropTypes.number,
  setCircle: PropTypes.func,
};

export default GPSLocationTracking;
